/**
 * Thème MUI global de l'app (mode clair).
 *
 * Jusqu'ici l'app principale n'avait AUCUN ThemeProvider : tous les composants
 * MUI tombaient sur le thème par défaut (palette bleue, typo Roboto). Ce fichier
 * fournit un thème unique, branché sur les couleurs du club (ClubThemeContext),
 * qui recolore et remodernise toute l'app depuis un seul endroit.
 *
 * Direction visuelle : on reprend le langage du stack Cantera v2 / Instat
 * (typo système, coins arrondis, ombres douces, boutons sans MAJUSCULES) mais
 * en MODE CLAIR, car l'app principale est claire — passer en sombre globalement
 * serait une régression massive. Le stack stats v2 (StatsRouter) et la LoginPage
 * gardent leur propre ThemeProvider et restent donc isolés de ce thème.
 *
 * Principe : on ne surcharge QUE ce qu'on veut changer. Tout le reste retombe
 * sur les défauts MUI — donc identique à aujourd'hui (spacing 8px, breakpoints,
 * fond de page, etc.). Le diff visible = palette club + typo système + look des
 * primitives ci-dessous.
 */

import { createTheme } from '@mui/material/styles';

// Stack système — même base que le stack stats v2, pour un rendu natif et net.
const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Échelle d'ombres douces (mode clair), appliquée seulement aux surfaces qu'on
// surcharge. On ne touche pas au tableau complet des 25 élévations MUI.
const SHADOW_SM = '0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)';
const SHADOW_MD = '0 4px 12px rgba(16,24,40,0.08)';

export function buildAppTheme({ primaryColor = '#F25F29', secondaryColor = '#FF7043' } = {}) {
  return createTheme({
    palette: {
      mode: 'light',
      primary:   { main: primaryColor,   contrastText: '#FFFFFF' },
      secondary: { main: secondaryColor, contrastText: '#FFFFFF' },
    },

    typography: {
      fontFamily: FONT_FAMILY,
      button: { textTransform: 'none', fontWeight: 600 },
    },

    shape: {
      // On garde le défaut MUI (4). Les écrans legacy utilisent souvent
      // `sx={{ borderRadius: N }}` (nombre) que MUI multiplie par cette valeur ;
      // la changer redimensionnerait TOUS les arrondis existants. L'arrondi
      // moderne est appliqué par composant ci-dessous, en pixels bruts.
      borderRadius: 4,
    },

    components: {
      // Boutons — la signature "moderne" : pas de MAJUSCULES, pas d'ombre, arrondis.
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, fontWeight: 600, textTransform: 'none' },
        },
      },

      // Surfaces arrondies (Paper sert de base aux menus, popovers, cards…).
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 12 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 14, boxShadow: SHADOW_MD },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 16 },
        },
      },

      // Champs de formulaire — arrondis cohérents avec les boutons.
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },

      // Petits éléments.
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 8,
            boxShadow: SHADOW_SM,
          },
        },
      },
    },
  });
}

export default buildAppTheme;
